import { useAuth0 } from "@auth0/auth0-react";
import { Button, Modal, notification, Table, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ReactSVG } from "react-svg";
import { useContextState } from "../../../context/ContextProvider"; import { addToNestedHeader } from "../../../features/nested_header/nested_header"; import AddToArchiveModal from "../../../modals/AddToArchiveModal";
import ScheduleToArchiveModal from "../../../modals/ScheduleToArchiveModal";
import Search from "../../../shared/Search";
import { addToArchive } from "../../../utils/archive_files";
import { getFolders, getQboFolders } from "../../../utils/organization";
import { organisationPage } from "../constants";

const Folder = ({ orgId, onFolderEnter, onBack ,onAttachmentSelected, type = "xero" }) => {
  const { isLoading } = useAuth0();
  const { getIdTokenClaims } = useAuth0();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedRowName, setSelectedRowName] = useState(""); // Track selected row's name
  const { setNestedHeader, setBreadCrumb, breadCrumb } = useContextState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [backupDates, setBackupDates] = useState([]);
  const [folderSize, setFolderSize] = useState({});
  const [folderSizeBreakdown, setFolderSizeBreakdown] = useState({});
  const [tableLoading, setTableLoading] = useState(true);
  const [archivedRows, setArchivedRows] = useState([]); // Track archived rows
  const [isArchiving, setIsArchiving] = useState(false);
  const [orgPlan, setOrgPlan] = useState({ files: 0, history_and_notes: 0, payroll: 0 });

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoading && orgId) {
      fetchFolderData();
    }
  }, [isLoading, orgId]);

  const fetchFolderData = async () => {
    try {
      setTableLoading(true);
      const token = await getIdTokenClaims();
      const fetchApi = type === "qbo" ? getQboFolders : getFolders;
      const [folderResponse] = await Promise.all([
        fetchApi(token.__raw, orgId),
      ]);
      setBackupDates(folderResponse.data.dates);
      setFolderSize(folderResponse.data.folder_size || {});
      setFolderSizeBreakdown(folderResponse.data.folder_size_breakdown || {});
      // Extract plan info from org data (xero or qbo object returned by API)
      const orgData = folderResponse.data?.plan ?? folderResponse.data?.xero ?? folderResponse.data?.qbo ?? {};
      setOrgPlan({
        files: Number(orgData.files ?? 0),
        history_and_notes: Number(orgData.history_and_notes ?? 0),
        payroll: Number(orgData.payroll ?? 0),
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      notification.error({
        message: "Error",
        description: "Failed to load folder data",
        duration: 2,
      });
    } finally {
      setTableLoading(false);
    }
  };

  /**
   * Given a backup queue row status and the org plan,
   * derive per-module subscription status for display.
   *
   * Status hierarchy per module:
   *   - Accounting is always subscribed.
   *   - Files / H&N / Payroll only shown if orgPlan flag = 1.
   *   - "running"              → Accounting = In Progress, others = Pending
   *   - "completed"            → Accounting = Completed, Files/H&N/Payroll = Completed (if subscribed)
   *   - "file-folder-pending"  → Accounting = Completed, Files = Pending, H&N/Payroll = Completed
   *   - "files-processing"     → Accounting = Completed, Files = In Progress, H&N/Payroll = Completed
   *   - "history-notes-pending"→ Accounting = Completed, Files = Completed, H&N = Pending, Payroll = Completed
   *   - "history-notes-processing" → Accounting = Completed, Files = Completed, H&N = In Progress, Payroll = Completed
   *   - "attachments-processing" → Accounting = Completed, others = Completed
   *   - "pending"              → all = Pending
   *   - "paused*"              → show warning
   */
  const deriveModuleStatuses = (status) => {
    const PENDING = "pending";
    const RUNNING = "running";
    const DONE = "completed";
    const ON_HOLD = "on_hold";
    const FAILED = "failed";

    // Chronological execution order:
    // 1. backup.py runs Accounting & Payroll together
    // 2. runFilePythonScript.php runs Files & Folders
    // 3. runhistoryPythonScript.php runs History & Notes
    const modules = [
      { key: "accounting", label: "Accounting", subscribed: true },
      { key: "payroll", label: "Payroll", subscribed: !!orgPlan.payroll },
      { key: "files", label: "Files & Folders", subscribed: !!orgPlan.files },
      { key: "history_and_notes", label: "History & Notes", subscribed: !!orgPlan.history_and_notes },
    ].filter((m) => m.subscribed);

    const statusMap = {};
    const lowerStatus = (status || "").toLowerCase();

    if (!lowerStatus || lowerStatus === "pending") {
      // Nothing has started yet — all pending
      modules.forEach((m) => { statusMap[m.key] = PENDING; });
    } else if (lowerStatus === "running") {
      // backup.py in progress: Accounting and Payroll are BOTH in progress
      statusMap.accounting = RUNNING;
      if (orgPlan.payroll) statusMap.payroll = RUNNING;
      if (orgPlan.files) statusMap.files = PENDING;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = PENDING;
    } else if (lowerStatus === "paused") {
      // Paused during backup.py: Accounting and Payroll are On Hold
      statusMap.accounting = ON_HOLD;
      if (orgPlan.payroll) statusMap.payroll = ON_HOLD;
      if (orgPlan.files) statusMap.files = PENDING;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = PENDING;
    } else if (lowerStatus === "file-folder-pending") {
      // backup.py completed (Accounting & Payroll done), Files waiting to start
      statusMap.accounting = DONE;
      if (orgPlan.payroll) statusMap.payroll = DONE;
      if (orgPlan.files) statusMap.files = PENDING;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = PENDING;
    } else if (lowerStatus === "files-processing") {
      // Accounting & Payroll done, Files in progress, H&N waiting
      statusMap.accounting = DONE;
      if (orgPlan.payroll) statusMap.payroll = DONE;
      if (orgPlan.files) statusMap.files = RUNNING;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = PENDING;
    } else if (lowerStatus === "paused_with_file_folder") {
      // Paused during Files backup
      statusMap.accounting = DONE;
      if (orgPlan.payroll) statusMap.payroll = DONE;
      if (orgPlan.files) statusMap.files = ON_HOLD;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = PENDING;
    } else if (lowerStatus === "history-notes-pending") {
      // Accounting, Payroll, and Files done; H&N waiting to start
      statusMap.accounting = DONE;
      if (orgPlan.payroll) statusMap.payroll = DONE;
      if (orgPlan.files) statusMap.files = DONE;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = PENDING;
    } else if (lowerStatus === "history-notes-processing") {
      // Accounting, Payroll, and Files done; H&N in progress
      statusMap.accounting = DONE;
      if (orgPlan.payroll) statusMap.payroll = DONE;
      if (orgPlan.files) statusMap.files = DONE;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = RUNNING;
    } else if (lowerStatus === "paused_with_history_notes") {
      // Paused during History & Notes
      statusMap.accounting = DONE;
      if (orgPlan.payroll) statusMap.payroll = DONE;
      if (orgPlan.files) statusMap.files = DONE;
      if (orgPlan.history_and_notes) statusMap.history_and_notes = ON_HOLD;
    } else if (lowerStatus === "completed" || lowerStatus === "attachments-processing") {
      // All phases finished
      modules.forEach((m) => { statusMap[m.key] = DONE; });
    } else if (lowerStatus.includes("fail") || lowerStatus.includes("error")) {
      modules.forEach((m) => { statusMap[m.key] = FAILED; });
    } else {
      modules.forEach((m) => { statusMap[m.key] = PENDING; });
    }

    return modules.map((m) => ({ ...m, status: statusMap[m.key] ?? PENDING }));
  };

  const ModuleStatusIcon = ({ status, label }) => {
    let icon = null;

    if (status === "pending") {
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-ellipsis text-sky-400 w-5 h-5">
          <circle cx="12" cy="12" r="10" />
          <path d="M17 12h.01" />
          <path d="M12 12h.01" />
          <path d="M7 12h.01" />
        </svg>
      );
    } else if (status === "running") {
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-loader-circle text-amber-500 w-5 h-5 animate-spin">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      );
    } else if (status === "completed") {
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-check-big text-green-500 w-5 h-5">
          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
          <path d="m9 11 3 3L22 4"></path>
        </svg>
      );
    } else if (status === "on_hold") {
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-pause text-amber-500 w-5 h-5">
          <circle cx="12" cy="12" r="10" />
          <line x1="10" x2="10" y1="15" y2="9" />
          <line x1="14" x2="14" y1="15" y2="9" />
        </svg>
      );
    } else {
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x text-red-500 w-5 h-5">
          <circle cx="12" cy="12" r="10" />
          <path d="m15 9-6 6" />
          <path d="m9 9 6 6" />
        </svg>
      );
    }

    const readableStatus = status === "running" ? "In Progress" : status === "completed" ? "Complete" : status === "on_hold" ? "On Hold" : status === "pending" ? "Pending" : "Failed";

    return (
      <Tooltip title={`${label}: ${readableStatus}`} placement="top">
        <div className="flex items-center gap-1.5 px-0.5 py-0.5 whitespace-nowrap cursor-pointer">
          <div className="flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <span className="text-[12px] font-medium text-gray-700">{label}</span>
        </div>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (key, { rec_date, is_archived }) => {
        if (rec_date) {
          return (
            <div className={"cursor-pointer flex justify-between items-center"}>
              <div
                onClick={() => {
                  onFolderEnter(rec_date);
                  const nextPage = {
                    header: rec_date,
                    page: organisationPage.ORGANISATION_FOLDER_CONTENTS,
                  };
                  dispatch(addToNestedHeader(nextPage));
                  setNestedHeader(nextPage);
                  setBreadCrumb((prevState) => [
                    ...prevState,
                    { label: rec_date, url: window.location.href },
                  ]);
                }}
                className={"flex items-center gap-3 py-1.5"}
              >
                <ReactSVG src={"/assets/icons/folder.svg"} />
                {rec_date}
              </div>
            </div>
          );
        }
      },
    },
    {
      title: "Folder Size",
      dataIndex: "rec_date",
      key: "folder_size",
      width: 185,
      render: (rec_date) => {
        const sizeVal = folderSize[rec_date];
        const bd = folderSizeBreakdown[rec_date];

        if (!sizeVal && !bd) {
          return <span className="text-gray-400">0 B</span>;
        }

        const hasFilesSub = !!(orgPlan.files || bd?.has_files);
        const hasHistorySub = !!(orgPlan.history_and_notes || bd?.has_history);

        const accountingMb = bd?.accounting_mb ?? (typeof sizeVal === "number" ? sizeVal : parseFloat(sizeVal) || 0);
        const filesMb = hasFilesSub ? (bd?.files_mb ?? 0) : 0;
        const historyMb = hasHistorySub ? (bd?.history_mb ?? 0) : 0;
        const totalMb = (hasFilesSub || hasHistorySub)
          ? (bd?.total_mb ?? (accountingMb + filesMb + historyMb))
          : accountingMb;

        const tooltipContent = (
          <div className="p-1 text-xs text-left leading-relaxed text-gray-200" style={{ minWidth: 180 }}>
            <div className="font-semibold text-white border-b border-gray-600 pb-1 mb-1.5 flex items-center justify-between">
              <span>Storage Breakdown</span>
              <span className="font-bold text-sky-400">{totalMb.toFixed(2)} MB</span>
            </div>
            <div className="flex justify-between items-center py-0.5">
              <span className="text-gray-300">Accounting:</span>
              <span className="font-medium text-white">{accountingMb.toFixed(2)} MB</span>
            </div>
            {hasFilesSub && (
              <div className="flex justify-between items-center py-0.5">
                <span className="text-gray-300">Files & Attachments:</span>
                <span className="font-medium text-white">{filesMb.toFixed(2)} MB</span>
              </div>
            )}
            {hasHistorySub && (
              <div className="flex justify-between items-center py-0.5">
                <span className="text-gray-300">History & Notes:</span>
                <span className="font-medium text-white">{historyMb.toFixed(2)} MB</span>
              </div>
            )}
          </div>
        );

        return (
          <Tooltip title={tooltipContent} placement="topLeft">
            <div className="cursor-pointer inline-flex items-center py-1">
              <span className="font-semibold text-sm text-gray-800 hover:text-blue-600 transition-colors">
                {totalMb.toFixed(2)} MB
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Subscription Type",
      dataIndex: "subscription_type",
      key: "subscription_type",
      width: 170,
      render: (subscription_type) => {
        if (!subscription_type) {
          return <span className="text-gray-400 text-xs italic">N/A</span>;
        }
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            {subscription_type}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 500,
      render: (status, record) => {
        const moduleStatuses = deriveModuleStatuses(status);

        return (
          <div className="flex items-center flex-wrap gap-x-3.5 gap-y-1 py-1">
            {moduleStatuses.map((m) => (
              <ModuleStatusIcon key={m.key} status={m.status} label={m.label} />
            ))}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      width: 400,
      render: (text, record) => {
        const { rec_date, is_archived } = record;
        return (
          <div className="flex items-center justify-end gap-4">
            <Button
              disabled={
                Number(is_archived) === 1 || archivedRows.includes(rec_date)
              } // Disable if archived or already in archive
              onClick={() => {
                setSelectedRowName(rec_date);
                setArchiveModalOpen(true);
              }}
              className="text-xs flex items-center gap-2 px-4 py-1.5 text-gray-800 bg-white border border-gray-300 rounded-lg font-bold"
            >
              <ReactSVG src={"/assets/icons/archive.svg"} />
              Add to Archive
            </Button>
            <Button
                className=" border border-gray-300 rounded-lg font-bold flex justify-start shadow-none text-sm"
                onClick={() => {
                  const nextPage = {
                    // header: account,
                    header: '',
                    page: organisationPage.ORGANISATION_ATTACHMENTS,
                  };
                  dispatch(addToNestedHeader(nextPage));
                  setNestedHeader(nextPage);
                  setBreadCrumb([]);
                  setBreadCrumb((prevState) => [
                    ...prevState,
                    { label: '', url: window.location.href },
                    // { label: account, url: window.location.href },
                  ]);
                  onAttachmentSelected(orgId, type);
                }}
              >
                <ReactSVG src={"/assets/icons/folder.svg"} />
                View Attachments
            </Button>
          </div>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
      if (selectedRows.length > 0) {
        setSelectedRowName(selectedRows[0].rec_date);
      }
    },
  };

  const onConfirmArchive = async () => {
    setIsArchiving(true);
    try {
      const token = await getIdTokenClaims();

      const response = await addToArchive(
        token.__raw,
        orgId,
        selectedRowName,
        type
      );
      if (response.data.success) {
        notification.success({
          message: "Backup Added to Archive",
          description: `${selectedRowName} has been successfully added to the archive.`,
          duration: 2,
          placement: "bottomRight",
        });
        // Add the archived row to the archivedRows state
        setArchivedRows((prev) => [...prev, selectedRowName]);
      } else {
        notification.error({
          message: "Failed to Archive Backup",
          description:
            "There was an error adding the backup to the archive. Please try again.",
          duration: 2,
        });
      }
    } catch (e) {
      notification.error({
        message: "Failed to Archive Backup",
        description:
          "There was an error adding the backup to the archive. Please try again.",
        duration: 2,
      });
    } finally {
      setIsArchiving(false);
      setArchiveModalOpen(false);
    }
  };

  const handleSearchChange = (value) => {};

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <>
      <AddToArchiveModal
        orgId={orgId}
        modalOpen={archiveModalOpen}
        selectedRowName={selectedRowName}
        onModalClose={() => {
          setArchiveModalOpen(false);
        }}
        onConfirm={onConfirmArchive}
        isArchiving={isArchiving}
      />
      <Modal
        title={[<div className={"text-2xl"}>Archive Schedule</div>]}
        centered
        open={scheduleModalOpen}
        footer={[
          <div className="flex items-center justify-end gap-3">
            <Button
              key="cancel"
              className="px-2 text-sm py-1.5 border border-gray-200 rounded-lg"
              onClick={() => setScheduleModalOpen(false)}
            >
              Cancel
            </Button>

            <Button
              key="submit"
              className="px-2 text-sm py-1.5 bg-blue-600 text-white rounded-lg"
              onClick={() => setScheduleModalOpen(false)}
            >
              Schedule to Archive
            </Button>
          </div>,
        ]}
        onOk={() => setScheduleModalOpen(false)}
        onCancel={() => setScheduleModalOpen(false)}
      >
        <ScheduleToArchiveModal organizationName={"WOW Backup & Restore"} />
      </Modal>
      <div className="lg:flex items-center justify-end">
        {selectedRowKeys.length > 0 && (
          <div className={"flex items-center gap-3"}>
            <Button className="text-xs flex items-center gap-2 lg:px-4 px-2 py-1 lg:py-1.5 text-gray-800 bg-white border border-gray-300 rounded-lg font-bold">
              <ReactSVG src={"/assets/icons/archive.svg"} />
              Add to Archive
            </Button>
            <Button className="text-xs flex items-center gap-2 lg:px-4 px-2 py-1 lg:py-1.5 text-gray-800 bg-white border border-gray-300 rounded-lg font-bold">
              <ReactSVG src={"/assets/icons/schedule.svg"} />
              Schedule to Archive
            </Button>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center my-2">
        <div className="flex items-center gap-2">
          <Button onClick={onBack} className="flex items-center" type="default">
            <ReactSVG src="/assets/icons/arrow-left.svg" />
            <span className="ml-2">Back</span>
          </Button>

          <div className="flex items-center">
            {breadCrumb.map((item, index) => {
              const isLastItem = index === breadCrumb.length - 1;
              return (
                <div key={index} className="flex items-center">
                  <p className={isLastItem ? "font-semibold" : ""}>
                    {item.label}
                  </p>
                  {!isLastItem && <span className="mx-2">&gt;</span>}
                </div>
              );
            })}
          </div>
        </div>

        <Search
          fullWidth={window.innerWidth < 600}
          placeholder="Search by module, record or file type..."
          handleChange={handleSearchChange}
        />
      </div>
      <Table
        sticky={true}
        check
        dataSource={backupDates}
        rowSelection={{
          type: "radio",
          columnWidth: 0,
          renderCell: () => null,
          columnTitle: null,
          selectedRowKeys: selectedRowKeys,
          onChange: (selectedKeys, selectedRows) => {
            setSelectedRowKeys(selectedKeys);
            if (selectedRows.length > 0) {
              setSelectedRowName(selectedRows[0].rec_date);
            }
          },
        }}
        columns={columns}
        loading={tableLoading}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          defaultPageSize: 10,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </>
  );
};

export default Folder;
